import {AfterViewInit, ChangeDetectorRef, Component, Inject, OnInit, ViewChild} from '@angular/core';
import {MatSort} from '@angular/material/sort';
import {MatPaginator} from '@angular/material/paginator';
import {SearchFilterComponent} from '@shared/components/search-filter/search-filter.component';
import {MatTableDataSource} from '@angular/material/table';
import {SchoolingTuition, SchoolingTuitionDetails} from '@shared/models/schooling-tuition.model';
import {merge} from 'rxjs';
import {MatDialog, MatDialogConfig} from '@angular/material/dialog';
import {ActivatedRoute, Router} from '@angular/router';
import {TranslationService} from '@shared/services/translation.service';
import {PermissionService} from '@shared/services/permission.service';
import {MatSnackBar} from '@angular/material/snack-bar';
import {
  checkPageSize,
  DEFAULT_PAGE_SIZE,
  filterLength,
  getTableColumns,
  loadSavedFilter,
  PAGE_SIZE_OPTIONS,
  pageChange,
  saveFilter
} from '@shared/utils/utils';
import {SchoolingTuitionService} from '@shared/services/schooling-tuition.service';
import {filter, tap} from 'rxjs/operators';
import {ConfirmDialogComponent, ConfirmDialogConfig} from '@shared/components/confirm-dialog/confirm-dialog.component';
import {Store} from '@ngxs/store';
import {ListViewComponent} from '@shared/components/list-view/list-view.component';
import {PermissionKeys} from '@shared/models/permission.model';
import {
  SchoolingTuitionParticipantsDialogsComponent
} from '@frontend/modules/schooling/schooling-tuition-participants-dialogs/schooling-tuition-participants-dialogs.component';
import {TableColumnDialogComponent} from '@shared/components/table-column-dialog/table-column-dialog.component';
import {EnumFilterOption} from '@shared/components/default-enum-filter/default-enum-filter.component';

@Component({
    selector: 'saf-schooling-tuition-list',
    templateUrl: './schooling-tuition-list.component.html',
    styleUrls: ['./schooling-tuition-list.component.scss'],
    standalone: false
})
export class SchoolingTuitionListComponent implements OnInit, AfterViewInit {
  @ViewChild(MatSort, {static: true}) sort: MatSort;
  @ViewChild(MatPaginator, {static: true}) paginator: MatPaginator;
  @ViewChild(SearchFilterComponent) searchFilter: SearchFilterComponent;
  @ViewChild(ListViewComponent, {static: false}) listView: ListViewComponent;
  listName = 'SchoolingTuitionList';
  public tableColumns = getTableColumns(this.listName) ||
    ['topicName', 'creator', 'lectureName', 'externalLecturerName', 'externalCompany', 'tuitionLocation', 'tuitionRoom', 'tuitionDate', 'tuitionTime',
      'status', 'participantsInfo', 'createDate', 'options'];
  public dataSource = new MatTableDataSource<SchoolingTuitionDetails>();
  public loading = false;
  public totalCount = 0;
  public hasPermission = false;
  public drawerFilterParams: any = {};
  public searchFilterParams: any = {};
  private readonly defaultStatusText = 'openAndOverdue';
  protected readonly pageChange = pageChange;
  protected readonly filterLength = filterLength;
  protected readonly checkPageSize = checkPageSize;

  statusFilterOptions: EnumFilterOption[] = [
    { label: 'all', value: 'all', isDefault: true },
    { label: 'openAndOverdue', value: 'openAndOverdue', iconClasses: ['saf-color-yellow', 'saf-color-red'] },
    { label: 'overdueTuition',  value: 'overdueTuition', iconClasses: ['saf-color-red'] },
    { label: 'openTuition', value: 'openTuition', iconClasses: ['saf-color-yellow'] },
    { label: 'completedTuition', value: 'completedTuition', iconClasses: ['saf-color-green'] },
    { label: 'canceledTuition', value: 'canceledTuition', iconClasses: ['saf-color-violet'] },
  ];

  public trackById = (item) => item.id;

  constructor(
    private schoolingTuitionService: SchoolingTuitionService,
    private dialog: MatDialog,
    private route: ActivatedRoute,
    private store: Store,
    private router: Router,
    private translationService: TranslationService,
    private changeDetectorRef: ChangeDetectorRef,
    private permissionService: PermissionService,
    private snackBar: MatSnackBar,
    @Inject(PAGE_SIZE_OPTIONS) public pageSizeOptions,
    @Inject(DEFAULT_PAGE_SIZE) public pageSize,
  ) {
  }

  ngOnInit(): void {
    this.hasPermission = this.permissionService.hasPermission(PermissionKeys.SCHOOLING_TUITIONS);
    this.ensureDefaultStatusFilter();
    this.sort.sortChange.subscribe(() => this.paginator.pageIndex = 0);
    merge(this.sort.sortChange, this.paginator.page).subscribe(() => this.loadTable());
  }

  ngAfterViewInit() {
    this.applyDrawerFilters(this.getInitialDrawerFilters());
    this.changeDetectorRef.detectChanges();
  }

  loadTable() {
    const params = {
      filter: {
        ...this.searchFilterParams,
        ...this.drawerFilterParams,
        showInternal: 1,
        showExternal: 1,
        withConditions: true
      },
      sort: this.sort.direction ? this.sort.active : '',
      dir: this.sort.direction,
      start: this.paginator.pageIndex * this.paginator.pageSize,
      limit: this.paginator.pageSize,
    };

    if (!this.drawerFilterParams.statusText) {
      params.filter.statusText = this.defaultStatusText;
    }
    this.loading = true;

    this.schoolingTuitionService.getListDetail(params)
      .pipe(
        tap(() => this.loading = false),
        tap((response) => {
          const enrichedData = response.data.map((item: SchoolingTuitionDetails) => ({
            ...item,
            requiredField: this.checkRequiredField(item)
          }));

          this.dataSource.data = enrichedData;
          this.totalCount = response.total;
        })
      )
      .subscribe();
  }

  reloadTable() {
    SchoolingTuitionService.clearCache();
    this.loadTable();
  }

  applyDrawerFilters(filterParams) {
    this.drawerFilterParams = filterParams;
    this.loadTable();
  }

  applySearchFilter({param, value}) {
    this.searchFilterParams = {[param]: value};

    if (value === null || value === undefined) {
      delete this.searchFilterParams[param];
    }

    this.loadTable();
  }

  openTuitionDetails(e, item?: SchoolingTuitionDetails) {
    e.stopPropagation();
    if (item) {
      this.router.navigate([item.tuition.id], { relativeTo: this.route });
      return;
    }

    if (!this.hasPermission) {
      return this.returnPermissionError();
    }

    this.router.navigate(['create'], { relativeTo: this.route });
  }

  openParticipantList(e, item?: SchoolingTuitionDetails) {
    e.stopPropagation();
    if (this.hasPermission) {
      this.router.navigate([item.tuition.id ], {relativeTo: this.route, queryParams: {tabIndex: 1}});
    } else {
      this.returnPermissionError();
    }
  }

  openFileList(e, item?: SchoolingTuitionDetails) {
    e.stopPropagation();
    if (this.hasPermission) {
      this.router.navigate([item.tuition.id ], {relativeTo: this.route, queryParams: {tabIndex: 2}});
    } else {
      this.returnPermissionError();
    }
  }

  openDeleteDialog(e, item: SchoolingTuitionDetails) {
    e.stopPropagation();
    if (this.hasPermission) {
      const data = new ConfirmDialogConfig({
        title: this.translationService.translate('confirmDeleteTitle'),
        message: this.translationService.translate('confirmDeleteTuition'),
        confirmText: this.translationService.translate('confirm'),
      });

      this.dialog.open(ConfirmDialogComponent, {data})
        .afterClosed()
        .pipe(filter(confirmed => !!confirmed))
        .subscribe(() => {
          this.schoolingTuitionService.delete(item.tuition.id)
            .subscribe(() => this.loadTable());
        });
    } else {
      this.returnPermissionError();
    }
  }

  getParticipantInfo(item: SchoolingTuitionDetails) {
    if (item.tuition.maxParticipants === 0) {
      if (item.participants.length > 0) {
        return item.participants.length.toString();
      } else {
        return '-';
      }
    }
    return item.participants.length + ' / ' + item.tuition.maxParticipants;
  }

  finish(e, tuition: SchoolingTuition) {
    e.stopPropagation();

    const now = new Date();
    const tuitionDateTime = new Date(`${tuition.tuitionDate}T${tuition.tuitionTime}`);

    if (tuitionDateTime > now) {
      return this.snackBar.open(this.translationService.translate('dateIsInTheFuture'));
    }

    if (this.hasPermission) {

      if (tuition.tuitionDate && tuition.lecturerEmployeeId && tuition.tuitionTime) {
        const dialogConfig: MatDialogConfig = {
          panelClass: 'table-dialog',
          disableClose: true,
          data: {
            tuitionId: tuition.id,
            topicId: tuition.topicId,
            assigneeEmployeeId: tuition.assigneeEmployeeId
          }
        };
        this.dialog.open(SchoolingTuitionParticipantsDialogsComponent, dialogConfig)
          .afterClosed()
          .pipe(filter(success => success))
          .subscribe(() => {
            this.schoolingTuitionService.finish(tuition.id)
              .subscribe(() => this.reloadTable());
          });
      } else {
        this.snackBar.open(this.translationService.translate('returnRequiredFieldErrorTuition'));
        this.router.navigate([tuition.id], {relativeTo: this.route, queryParams: {tabIndex: 0}});

      }

    } else {
      this.returnPermissionError();
    }
  }

  returnPermissionError() {
    return this.snackBar.open(this.translationService.translate('permissionError'));
  }

  openPrint(e, item: SchoolingTuitionDetails) {
    e.stopPropagation();
    if (this.hasPermission) {
      // this.schoolingTuitionService.getPrintData(item.tuition.id).subscribe((blob) => {
      //   const url = window.URL.createObjectURL(blob);
      //   window.open(url, '_blank');
      // });
      window.open(this.schoolingTuitionService.getPrintURL(item.tuition.id, localStorage.getItem('jwt')), '_blank');
    } else {
      this.returnPermissionError();
    }
  }

  openExportPage() {
    if (this.hasPermission) {
      const filter = {
        ...this.searchFilterParams,
        ...this.drawerFilterParams,
        showInternal: 1,
        showExternal: 1,
        withConditions: true
      };

      if (!this.drawerFilterParams.statusText) {
        filter.statusText = this.defaultStatusText;
      }

      const frontendKeys = {
        ...this.tableColumns
      };

      const sortingParams = {
        sort: this.sort.direction ? this.sort.active : '',
        dir: this.sort.direction
      };

      window.open(this.schoolingTuitionService.getExportURL(filter, sortingParams, frontendKeys, localStorage.getItem('jwt')), '_blank');
    } else {
      this.returnPermissionError();
    }
  }

  checkDate(listItem: SchoolingTuitionDetails) {
    if (listItem.tuition.tuitionDate === null) {
      return true;
    }

    return new Date(new Date(listItem.tuition.tuitionDate).setHours(0, 0, 0, 0)).valueOf() >=
      new Date(new Date().setHours(0, 0, 0, 0)).valueOf();
  }

  checkRequiredField(tuition: SchoolingTuitionDetails) {
    return !!(tuition.tuition.tuitionDate && tuition.tuition.tuitionTime && tuition.tuition.lecturerEmployeeId);
  }

  openTableColumnDialog() {
    const dialogConfig: MatDialogConfig = {
      panelClass: 'table-dialog',
      disableClose: true,
      data: {
        listName: this.listName,
        activeColumns: this.tableColumns
      },
    };
    this.dialog.open(TableColumnDialogComponent, dialogConfig)
      .afterClosed().subscribe(data => {
      if (data === 'reset') { window.location.reload(); }
    });
  }

  private getInitialDrawerFilters() {
    return {
      statusText: this.defaultStatusText,
      ...loadSavedFilter(this.listName),
    };
  }

  private ensureDefaultStatusFilter() {
    const savedFilters = loadSavedFilter(this.listName);

    if (!savedFilters.statusText) {
      saveFilter({
        ...savedFilters,
        statusText: this.defaultStatusText,
      }, this.listName);
    }
  }
}
